"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useTraceStore, type Hop } from "@/store/useTraceStore";
import { PANEL, PHYSICS, CAMERA } from "@/config/constants";
import { captureGlobeScreenshot } from "@/lib/globe-capture";
import { generateJourneyCard } from "@/lib/generate-journey-card";
import HopDot from "./HopDot";
import LatencyBadge from "./LatencyBadge";

/* ─────────────────────── Text fallback for share ─────────────────────── */

function buildShareText(hops: Hop[], targetUrl: string): string {
  const origin = hops[0];
  const dest = hops[hops.length - 1];
  const totalLatency = dest.cumLatency;
  const totalDistance = hops.reduce(
    (sum, hop) => sum + Math.min(hop.distance, hop.latency * 300),
    0
  );
  const lightFiberMs = Math.round((totalDistance / PHYSICS.SPEED_OF_LIGHT_FIBER) * 1000);
  const lightEfficiency = totalLatency > 0 ? Math.round((lightFiberMs / totalLatency) * 100) : 0;
  const cables = [...new Set(hops.filter((h) => h.cable).map((h) => h.cable!.name))];

  return [
    `PacketVoyager — ${targetUrl}`,
    `${origin.city}, ${origin.country} → ${dest.city}, ${dest.country}`,
    `${hops.length} hops | ${totalDistance.toLocaleString()} km | ${totalLatency}ms | ${lightEfficiency}% speed of light`,
    ...(cables.length > 0 ? [`via ${cables.join(", ")}`] : []),
    ``,
    ...hops.map(
      (h) => `${h.num}. ${h.city} — ${h.ip} (${h.asnName}) ${h.cumLatency}ms`
    ),
  ].join("\n");
}

/* ─────────────────────── Sidebar Component ─────────────────────── */

interface SidebarProps {
  onReset: () => void;
}

export default function Sidebar({ onReset }: SidebarProps) {
  const phase = useTraceStore((s) => s.phase);
  const hops = useTraceStore((s) => s.hops);
  const probe = useTraceStore((s) => s.probe);
  const targetUrl = useTraceStore((s) => s.targetUrl);
  const activeHopIndex = useTraceStore((s) => s.activeHopIndex);
  const sidebarOpen = useTraceStore((s) => s.sidebarOpen);
  const setSidebarOpen = useTraceStore((s) => s.setSidebarOpen);
  const setActiveHopIndex = useTraceStore((s) => s.setActiveHopIndex);
  const setPhase = useTraceStore((s) => s.setPhase);
  const setCameraMode = useTraceStore((s) => s.setCameraMode);

  /* ──── Sidebar auto-scroll ──── */
  const hopRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Clear the ref map when a trace resets so stale DOM refs don't accumulate
  // across multiple traces (memory leak: refs are added on mount but never
  // removed when the hop element unmounts because React's ref callback only
  // runs with a non-null element on mount, not null on unmount).
  useEffect(() => {
    if (phase === "idle") {
      hopRefs.current.clear();
    }
  }, [phase]);

  useEffect(() => {
    if (phase === "animating" && activeHopIndex >= 0) {
      const el = hopRefs.current.get(activeHopIndex);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [activeHopIndex, phase]);

  /* ──── Navigate camera to a specific hop ──── */
  const navigateToHop = useCallback(
    (index: number) => {
      setActiveHopIndex(index);
      if (phase === "complete") {
        setCameraMode("follow");
      }
    },
    [phase, setActiveHopIndex, setCameraMode]
  );

  /* ──── Replay animation ──── */
  const handleReplay = useCallback(() => {
    setActiveHopIndex(0);
    setPhase("animating");
    setCameraMode("departure");
  }, [setActiveHopIndex, setPhase, setCameraMode]);

  /* ──── Share journey as image card ──── */
  const [shareLabel, setShareLabel] = useState("Share Journey");
  const [shareLoading, setShareLoading] = useState(false);

  const handleShare = useCallback(async () => {
    if (hops.length === 0 || shareLoading) return;
    setShareLoading(true);
    setShareLabel("Capturing...");

    // Pause animation while capturing to prevent camera conflicts
    const wasAnimating = useTraceStore.getState().phase === "animating";
    const savedHopIndex = useTraceStore.getState().activeHopIndex;
    if (wasAnimating) setPhase("complete");

    const alreadyAtReveal = useTraceStore.getState().cameraMode === "reveal";
    setCameraMode("reveal");
    // Wait for the full camera reveal transition to complete before capturing.
    // CAMERA.REVEAL_MS (3000ms) is the actual transition duration — using a
    // shorter value caused screenshots to capture a mid-animation globe.
    await new Promise((r) => setTimeout(r, alreadyAtReveal ? 500 : CAMERA.REVEAL_MS));

    try {
      const screenshot = captureGlobeScreenshot();
      if (!screenshot) throw new Error("Globe capture unavailable");

      const blob = await generateJourneyCard(screenshot, hops, targetUrl);
      const fileName = `packetvoyager-${targetUrl.replace(/[^a-z0-9.]/gi, "-")}.png`;
      const file = new File([blob], fileName, { type: "image/png" });

      if (navigator.share) {
        try {
          const shareData: ShareData = {
            title: `PacketVoyager — ${targetUrl}`,
            files: [file],
          };
          if (navigator.canShare?.(shareData)) {
            await navigator.share(shareData);
            setShareLabel("Shared!");
            setShareLoading(false);
            setTimeout(() => setShareLabel("Share Journey"), 2500);
            return;
          }
        } catch {
          // User cancelled or not supported — fall through to download
        }
      }

      // Fallback: download the image
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShareLabel("Saved to Downloads");
    } catch {
      // Last resort: copy text summary to clipboard
      try {
        await navigator.clipboard.writeText(buildShareText(hops, targetUrl));
        setShareLabel("Copied text!");
      } catch {
        setShareLabel("Failed");
      }
    }

    setShareLoading(false);
    setTimeout(() => setShareLabel("Share Journey"), 2500);

    // Resume animation if it was paused for the capture
    if (wasAnimating && savedHopIndex < hops.length - 1) {
      setActiveHopIndex(savedHopIndex);
      setPhase("animating");
    }
  }, [hops, targetUrl, shareLoading, setCameraMode, setPhase, setActiveHopIndex]);

  /* ──── Sidebar toggle (when closed) ──── */
  if (!sidebarOpen) {
    return (
      <button
        onClick={() => setSidebarOpen(true)}
        aria-label="Open journey log sidebar"
        className="absolute top-5 right-5 z-20 w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer lg:block"
        style={{ background: PANEL.BG, border: `1px solid ${PANEL.BORDER}` }}
      >
        <svg
          className="w-4 h-4 text-[#A1A1AA] mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    );
  }

  return (
    <aside
      className={[
        "absolute z-20 overflow-y-auto",
        // Desktop: right sidebar
        "lg:top-0 lg:right-0 lg:h-full lg:w-[360px] lg:animate-slide-in-right",
        // Mobile: bottom sheet
        "top-auto bottom-0 left-0 right-0 h-[55vh] lg:h-full lg:left-auto rounded-t-2xl lg:rounded-none animate-slide-in-bottom lg:animate-slide-in-right",
      ].join(" ")}
      aria-label="Journey log"
      style={{
        background: PANEL.BG_HEAVY,
        borderLeft: "1px solid rgba(255, 255, 255, 0.06)",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.3)",
      }}
    >
      <div className="p-4 lg:p-5 pb-8 space-y-3">
        {/* Mobile drag handle */}
        <div className="flex justify-center py-1 lg:hidden" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-[rgba(255,255,255,0.2)]" />
        </div>
        {/* Header */}
        <div className="space-y-1 pb-4 border-b border-[rgba(255,255,255,0.06)]">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#F4F4F5] tracking-[-0.01em]">Journey Log</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-[#71717A] hover:text-[#F4F4F5] transition-colors cursor-pointer"
              aria-label="Close sidebar"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <p className="text-[11px] text-[#71717A]">
            {hops.length > 0
              ? `${hops[0].city}, ${hops[0].country} → ${hops[hops.length - 1].city}`
              : "Tracing..."}
          </p>
          {probe && (
            <p className="text-[10px] text-[#52525B] mt-0.5">
              Traced from {probe.city}, {probe.country}
            </p>
          )}
        </div>

        {/* Hop cards — arrow keys navigate between hops */}
        <div
          className="space-y-1.5"
          role="list"
          aria-label="Network hops"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowRight") {
              e.preventDefault();
              const maxIdx = phase === "complete" ? hops.length - 1 : activeHopIndex;
              if (activeHopIndex < maxIdx) navigateToHop(activeHopIndex + 1);
            } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
              e.preventDefault();
              if (activeHopIndex > 0) navigateToHop(activeHopIndex - 1);
            }
          }}
        >
          {hops.map((hop, i) => {
            if (phase === "animating" && i > activeHopIndex) return null;
            const isActive = activeHopIndex === i;
            const prevLatency = i > 0 ? hops[i - 1].cumLatency : 0;
            const delta = hop.cumLatency - prevLatency;
            const borderColor =
              hop.type === "origin"
                ? "#FFA726"
                : hop.type === "ixp"
                  ? "#8B5CF6"
                  : hop.type === "destination"
                    ? "#10B981"
                    : isActive
                      ? "#06B6D4"
                      : "rgba(255,255,255,0.06)";

            return (
              <div
                key={hop.num}
                ref={(el) => {
                  if (el) hopRefs.current.set(i, el);
                }}
                role="listitem"
              >
                {/* Cable crossing card */}
                {hop.cable && (
                  <div
                    className="mb-1.5 p-3 rounded-lg"
                    style={{ background: "#27272A", borderLeft: "3px solid #06B6D4" }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <svg
                        className="w-3.5 h-3.5 text-[#06B6D4]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"
                        />
                      </svg>
                      <span className="text-[10px] text-[#06B6D4] font-medium uppercase tracking-wider">
                        Ocean Crossing
                      </span>
                    </div>
                    <div className="text-[13px] font-semibold text-[#F4F4F5] mb-1.5">
                      {hop.cable.name}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                      <div>
                        <span className="text-[#71717A]">Length </span>
                        <span className="text-[#A1A1AA] font-[family-name:var(--font-geist-mono)]">
                          {hop.cable.length.toLocaleString()} km
                        </span>
                      </div>
                      <div>
                        <span className="text-[#71717A]">Laid </span>
                        <span className="text-[#A1A1AA] font-[family-name:var(--font-geist-mono)]">
                          {hop.cable.year}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#71717A]">Owners </span>
                        <span className="text-[#A1A1AA]">{hop.cable.owners}</span>
                      </div>
                      <div>
                        <span className="text-[#71717A]">Capacity </span>
                        <span className="text-[#A1A1AA] font-[family-name:var(--font-geist-mono)]">
                          {hop.cable.capacity}
                        </span>
                      </div>
                    </div>
                    {hop.cable.name === "MAREA Cable" && (
                      <p className="mt-2 text-[11px] text-[#71717A] italic">
                        &ldquo;Enough for 71 million HD streams simultaneously&rdquo;
                      </p>
                    )}
                  </div>
                )}

                {/* Hop card */}
                <button
                  onClick={() => navigateToHop(i)}
                  aria-label={`Hop ${hop.num}: ${hop.city}, ${hop.country} — ${hop.latency}ms. Click to focus on globe.`}
                  aria-current={isActive ? "step" : undefined}
                  className={`w-full text-left p-3 rounded-lg transition-all duration-200 cursor-pointer ${
                    isActive ? "glow-panel" : ""
                  }`}
                  style={{
                    background: isActive ? "#27272A" : "transparent",
                    borderLeft: `3px solid ${borderColor}`,
                  }}
                >
                  <div className="flex items-start gap-2.5">
                    <HopDot type={hop.type} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-medium text-[#F4F4F5] truncate">
                          Hop {hop.num} — {hop.city}
                        </span>
                        <LatencyBadge ms={hop.latency} delta={delta} />
                      </div>
                      {hop.isIXP && hop.ixpName && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <svg
                            className="w-3 h-3 text-[#8B5CF6] shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="text-[11px] text-[#8B5CF6]">
                            {hop.ixpName}
                            {hop.ixpTraffic && ` (${hop.ixpTraffic})`}
                          </span>
                        </div>
                      )}
                      <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                        <div>
                          <span className="text-[#71717A]">IP </span>
                          <span className="text-[#A1A1AA] font-[family-name:var(--font-geist-mono)]">
                            {hop.ip}
                          </span>
                        </div>
                        <div>
                          <span className="text-[#71717A]">ASN </span>
                          <span className="text-[#A1A1AA]">{hop.asn}</span>
                        </div>
                        <div>
                          <span className="text-[#71717A]">Net </span>
                          <span className="text-[#A1A1AA]">{hop.asnName}</span>
                        </div>
                        {hop.distance > 0 && (
                          <div>
                            <span className="text-[#71717A]">Dist </span>
                            <span className="text-[#A1A1AA] font-[family-name:var(--font-geist-mono)]">
                              {hop.distance.toLocaleString()} km
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Journey Summary (only when complete and hops exist) */}
        {phase === "complete" &&
          hops.length > 0 &&
          (() => {
            const totalDistance = hops.reduce((sum, hop) => {
              const maxPlausible = hop.latency * 300;
              return sum + Math.min(hop.distance, maxPlausible);
            }, 0);
            const totalLatency = hops[hops.length - 1].cumLatency;
            const lightVacuumMs = Math.round(
              (totalDistance / PHYSICS.SPEED_OF_LIGHT_VACUUM) * 1000
            );
            const lightFiberMs = Math.round(
              (totalDistance / PHYSICS.SPEED_OF_LIGHT_FIBER) * 1000
            );
            const lightEfficiency =
              totalLatency > 0 ? Math.round((lightFiberMs / totalLatency) * 100) : 0;
            const processingOverhead = Math.max(totalLatency - lightFiberMs, 0);

            return (
              <>
                <div
                  className="p-4 rounded-lg space-y-3"
                  style={{
                    background: "#18181B",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <h3 className="text-[10px] text-[#71717A] font-medium uppercase tracking-wider">
                    Journey Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xl font-bold text-[#06B6D4] font-[family-name:var(--font-geist-mono)]">
                        {totalLatency}ms
                      </div>
                      <div className="text-[11px] text-[#71717A]">Total latency</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-[#F4F4F5] font-[family-name:var(--font-geist-mono)]">
                        {totalDistance.toLocaleString()}
                      </div>
                      <div className="text-[11px] text-[#71717A]">Kilometers</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-[#F4F4F5] font-[family-name:var(--font-geist-mono)]">
                        {hops.length}
                      </div>
                      <div className="text-[11px] text-[#71717A]">Hops</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-[#FFA726] font-[family-name:var(--font-geist-mono)]">
                        {lightEfficiency}%
                      </div>
                      <div className="text-[11px] text-[#71717A]">Speed of light</div>
                    </div>
                  </div>

                  {/* Speed of Light */}
                  <div
                    className="p-3 rounded-md space-y-2"
                    style={{
                      background: "rgba(6, 182, 212, 0.04)",
                      border: "1px solid rgba(6, 182, 212, 0.08)",
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-3.5 h-3.5 text-[#06B6D4]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span className="text-[10px] text-[#06B6D4] font-medium uppercase tracking-wider">
                        Speed of Light
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-[11px]">
                        <span className="text-[#A1A1AA]">Light in vacuum</span>
                        <span className="text-[#F4F4F5] font-[family-name:var(--font-geist-mono)]">
                          {lightVacuumMs}ms
                        </span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-[#A1A1AA]">Light in fiber</span>
                        <span className="text-[#F4F4F5] font-[family-name:var(--font-geist-mono)]">
                          {lightFiberMs}ms
                        </span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-[#A1A1AA]">Your packet</span>
                        <span className="text-[#06B6D4] font-medium font-[family-name:var(--font-geist-mono)]">
                          {totalLatency}ms
                        </span>
                      </div>
                      <div
                        className="w-full h-1.5 rounded-full bg-[#27272A] overflow-hidden mt-1"
                        role="progressbar"
                        aria-valuenow={lightEfficiency}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Speed of light efficiency"
                      >
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#06B6D4] to-[#0E7490]"
                          style={{ width: `${Math.min(lightEfficiency, 100)}%` }}
                        />
                      </div>
                      <p className="text-[11px] text-[#71717A] mt-1 leading-relaxed">
                        Your data traveled at {lightEfficiency}% the speed of light. The remaining{" "}
                        {processingOverhead}ms is router processing at each hop.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleShare}
                    disabled={shareLoading}
                    aria-label={shareLoading ? "Generating journey card" : "Share journey as image"}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium transition-colors cursor-pointer ${
                      shareLoading
                        ? "bg-[#164E63] text-[#22D3EE]"
                        : "bg-[#06B6D4] text-black hover:bg-[#22D3EE]"
                    }`}
                  >
                    {shareLoading ? (
                      <div
                        className="w-3.5 h-3.5 border-2 border-[#22D3EE]/30 border-t-[#22D3EE] rounded-full animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                        />
                      </svg>
                    )}
                    {shareLabel}
                  </button>
                  <button
                    onClick={handleReplay}
                    aria-label="Replay the journey animation"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium bg-[#18181B] text-[#F4F4F5] border border-[rgba(255,255,255,0.08)] hover:bg-[#27272A] transition-colors cursor-pointer"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Replay
                  </button>
                </div>
                <button
                  onClick={onReset}
                  aria-label="Start a new trace"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-[12px] text-[#71717A] hover:text-[#A1A1AA] transition-colors cursor-pointer"
                >
                  New Trace
                </button>
              </>
            );
          })()}
      </div>
    </aside>
  );
}
